/**
 * FutureDateOfServiceDialog — GDN-2784 sign-time guard confirmation.
 *
 * Shown when the backend blocks finalizing a note because its encounter's
 * date of service is materially in the future (usually: the note landed in
 * the encounter appointment-sync pre-created for an upcoming visit). The
 * default path repairs the record — official time becomes the actual
 * recording moment — rather than blessing the future date.
 */

import { formatDateLong } from '@features/encounters/components/OfficialTimeSelector/officialTimeModel';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

export interface FutureDateOfServiceDialogProps {
  open: boolean;
  /** The encounter's effective date of service, ISO "YYYY-MM-DD". */
  dateOfService: string | null;
  /** Verb for the retry buttons: "Sign", "Complete", "Resubmit", "Co-sign". */
  actionLabel: string;
  /**
   * Whether the repair path is offered. Co-sign is acknowledge-only — the
   * author's official time is not the supervisor's to change.
   */
  showUpdateDateOption: boolean;
  /** Disables the actions while a repair or retry is in flight. */
  isBusy: boolean;
  onUpdateDateAndProceed: () => void;
  onProceedAnyway: () => void;
  onCancel: () => void;
}

export const FutureDateOfServiceDialog = ({
  open,
  dateOfService,
  actionLabel,
  showUpdateDateOption,
  isBusy,
  onUpdateDateAndProceed,
  onProceedAnyway,
  onCancel,
}: FutureDateOfServiceDialogProps) => {
  const formattedDate = dateOfService ? formatDateLong(dateOfService) : null;

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onCancel}
      aria-labelledby="future-dos-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="future-dos-dialog-title">
        This note&apos;s visit is dated {formattedDate ?? 'in the future'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          The encounter&apos;s date of service is in the future — this note may
          have landed in an encounter created for an upcoming appointment.
          {showUpdateDateOption
            ? ' Updating the date of service stamps the actual session time on the note and claim.'
            : ''}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onCancel} disabled={isBusy} color="inherit">
          Cancel
        </Button>
        <Button onClick={onProceedAnyway} disabled={isBusy} color="warning">
          {actionLabel} anyway — keep {dateOfService ?? 'the future date'}
        </Button>
        {showUpdateDateOption && (
          <Button
            onClick={onUpdateDateAndProceed}
            disabled={isBusy}
            variant="contained"
            autoFocus
          >
            Update date of service &amp; {actionLabel.toLowerCase()}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
